import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMemo, useRef } from "react";
import { getRequiredFields } from "components/form/utils";
import { deliverableSchema } from "./schema";
import { fetchDeliverableData, toFormValues } from "./read";
import { useUpdateDeliverable } from "../update/mutation";
import { useCreateDeliverable } from "../create/mutation";

export const useDeliverableForm = ({ projectId }) => {
  const form = useForm({
    resolver: zodResolver(deliverableSchema),
    defaultValues: { name: "", type: "", url: "", files: [], skills: [] },
  });

  const requiredFields = useMemo(() => getRequiredFields(deliverableSchema), []);

  // related id -> junction row id, as loaded from the server
  const skillJunctionsRef = useRef(new Map());
  const fileJunctionsRef = useRef(new Map());

  const updateDeliverable = useUpdateDeliverable();
  const createDeliverable = useCreateDeliverable();

  const handleCreate = async (context) => {
    await createDeliverable.mutateAsync({
      item: { ...context.values, project: projectId },
    });
  };

  const handleUpdate = async (id, context) => {
    await updateDeliverable.mutateAsync({
      deliverableId: id,
      item: context.dirtyValues,
      skillJunctions: skillJunctionsRef.current,
      fileJunctions: fileJunctionsRef.current,
    });
  };

  const handleRead = async (id) => {
    const response = await fetchDeliverableData({ deliverableId: id });
    const { values, skillJunctions, fileJunctions } = toFormValues(response);
    skillJunctionsRef.current = skillJunctions;
    fileJunctionsRef.current = fileJunctions;
    return values;
  };

  return { form, requiredFields, handleCreate, handleUpdate, handleRead };
};
